<template>
    <div>
        <div class="modal" id="erpSyncSettingLogsModal" data-backdrop="static" data-keyboard="false" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-custom">
                <div class="modal-content">
                    <div class="modal-header p-3">
                        <h5 class="modal-title text-grey-darker font-medium text-base">
                            Sync Logs
                            <i
                                class="fa fa-refresh hover:text-followup-nav cursor-pointer pl-2 mt-2"
                                title="Refresh"
                                @click="getERPSyncSettingLogs()">
                            </i>
                            <span v-if="vistaConnection" class="text-dark font-bold">({{vistaConnection.name}})</span>
                        </h5>
                        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body bg-white min-h-screen-60 px-4 py-3">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <!-- Filters -->
                            <div class="d-flex align-items-center">
                                <!-- Search Bar -->
                                <div class="min-w-15 search-container">
                                    <input type="text"
                                           v-model="filterSelected.search"
                                           class="form-control focus:border-followup-nav"
                                           placeholder="Search Object ID..."
                                           @input="debounceFilter">
                                    <i class="fa fa-search text-primary"></i>
                                </div>

                                <!-- Status Filter -->
                                <div :class="(filterSelected.status.length > 0 ? 'show-filter-selected ml-2': 'pl-3')">
                                    <div class="dropdown">
                                        <a  class="no-link-properties dropdown-toggle pointer"
                                            id="statusPopoverButton"
                                            tabindex="0">
                                            <span>{{ filterStatusLabel }}</span>
                                        </a>
                                        <span
                                            v-if="filterSelected.status.length > 0"
                                            class="ml-2 text-grey pointer remove-filter-span"
                                            @click.prevent="filterSelected.status = []">
                                                &#x2715;
                                        </span>
                                        <!-- Dropdown Options -->
                                        <b-popover target="statusPopoverButton" placement="bottom" custom-class="filter-popover-class" triggers="focus">
                                            <div class="justify-content-center my-2">
                                                <div v-for="(item, index) in filterOptions.status" :key="`checkbox-status-group-${index}`"
                                                     class="px-3 pt-2 pb-1 hover:theme-custom-background hover:theme-custom-color">
                                                    <label class="custom-checkbox">
                                                        <input type="checkbox" v-model="filterSelected.status" :value="item.value" />
                                                        <span class="checkmark"></span>
                                                        {{ item.text }}
                                                    </label>
                                                </div>
                                            </div>
                                        </b-popover>
                                    </div>
                                </div>

                                <!-- Followup Object Filter -->
                                <div :class="(filterSelected.followupObject.length > 0 ? 'show-filter-selected ml-2': 'pl-3')">
                                    <div class="dropdown">
                                        <a  class="no-link-properties dropdown-toggle pointer"
                                            id="followupObjectPopoverButton"
                                            tabindex="0">
                                            <span>{{ filterFollowupObjectLabel }}</span>
                                        </a>
                                        <span
                                            v-if="filterSelected.followupObject.length > 0"
                                            class="ml-2 text-grey pointer remove-filter-span"
                                            @click.prevent="filterSelected.followupObject = []">
                                                &#x2715;
                                        </span>
                                        <!-- Dropdown Options -->
                                        <b-popover target="followupObjectPopoverButton" placement="bottom" custom-class="filter-popover-class" triggers="focus">
                                            <div class="justify-content-center my-2">
                                                <div v-for="(item, index) in filterOptions.followupObject" :key="`checkbox-status-group-${index}`"
                                                     class="px-3 pt-2 pb-1 hover:theme-custom-background hover:theme-custom-color">
                                                    <label class="custom-checkbox">
                                                        <input type="checkbox" v-model="filterSelected.followupObject" :value="item.value" />
                                                        <span class="checkmark"></span>
                                                        {{ item.text }}
                                                    </label>
                                                </div>
                                            </div>
                                        </b-popover>
                                    </div>
                                </div>

                                <!-- ERP Object Filter -->
                                <div :class="(filterSelected.erpObject.length > 0 ? 'show-filter-selected ml-2': 'pl-3')">
                                    <div class="dropdown">
                                        <a  class="no-link-properties dropdown-toggle pointer"
                                            id="erpObjectPopoverButton"
                                            tabindex="0">
                                            <span class="capitalize">{{ filterERPObjectLabel }}</span>
                                        </a>
                                        <span
                                            v-if="filterSelected.erpObject.length > 0"
                                            class="ml-2 text-grey pointer remove-filter-span"
                                            @click.prevent="filterSelected.erpObject = []">
                                                &#x2715;
                                        </span>
                                        <!-- Dropdown Options -->
                                        <b-popover target="erpObjectPopoverButton" placement="bottom" custom-class="filter-popover-class" triggers="focus">
                                            <div class="justify-content-center my-2">
                                                <div v-for="(item, index) in filterOptions.erpObject" :key="`checkbox-status-group-${index}`"
                                                     class="px-3 pt-2 pb-1 hover:theme-custom-background hover:theme-custom-color">
                                                    <label class="custom-checkbox">
                                                        <input type="checkbox" v-model="filterSelected.erpObject" :value="item.value" />
                                                        <span class="checkmark"></span>
                                                        {{ item.text }}
                                                    </label>
                                                </div>
                                            </div>
                                        </b-popover>
                                    </div>
                                </div>
                            </div>

                            <!-- Clear Filters -->
                            <div>
                                <button class="btn btn-outline-primary" @click="clearFilters">Clear filters</button>
                            </div>
                        </div>

                        <!-- Actions & Download CSV -->
                        <div class="d-flex justify-content-between align-items-center my-3">
                            <div>
                                <div v-if="selectedLogIds.length > 0">
                                    <span class="text-grey-darkest">{{ selectedLogIds.length }} Record(s) Selected</span>
                                    <button v-if="ignoreButtonEnabled" class="btn btn-outline-secondary ml-2" @click.prevent="ignoreSelectedErrorLogs">Ignore</button>
                                    <button v-if="retrySyncButtonEnabled" class="btn btn-outline-primary ml-2" @click.prevent="retrySync">Retry Sync</button>
                                </div>
                            </div>
                            <div>
                                <button class="btn btn-outline-secondary mr-2" @click.prevent="ignoreAllErrorLogs">Ignore all Errors</button>
                                <button class="btn btn-outline-primary" @click.prevent="exportAllErrorLogs">Download Error CSV</button>
                            </div>
                        </div>

                        <div v-if="loader" class="loader-overlay">
                            <div class="spinner-border text-primary" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                        </div>
                        <table v-if="!loader" class="table" id="erpSyncLogsTable">
                            <thead>
                            <tr>
                                <th class="align-middle">
                                    <label class="custom-checkbox">
                                        <input type="checkbox" id="selectAllLogsCheckbox" @click="manageLogIdsSelection" />
                                        <span class="checkmark"></span>
                                    </label>
                                </th>
                                <th class="align-middle capitalize">{{ erpIntegrationName }} Object</th>
                                <th class="align-middle">Followup Object</th>
                                <th class="align-middle">Followup Object Link</th>
                                <th class="align-middle">Followup ID</th>
                                <th class="align-middle">Last Attempted Sync</th>
                                <th class="align-middle">Status</th>
                                <th class="align-middle capitalize">{{ erpIntegrationName }} ID</th>
                                <th class="align-middle">Direction</th>
                                <th class="align-middle">Details</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="log in syncLogs" :key="log.id">
                                <td>
                                    <label class="custom-checkbox">
                                        <input type="checkbox" v-model="selectedLogIds" :value="log.id" />
                                        <span class="checkmark"></span>
                                    </label>
                                </td>
                                <td>{{ formatERPObjectLabel(log.erp_object) }}</td>
                                <td class="capitalize">{{ log.followup_object.split('-').join(' ') }}</td>
                                <td><a :href="followupObjectUrl(log)" target="_blank">{{ log.followup_object_name }}</a></td>
                                <td>{{ log.followup_object_id }}</td>
                                <td>{{ formattedTime(log.sync_time) }}</td>
                                <td><span class="status-badge capitalize" :class="(log.status === 'success' ? 'status-success' : (log.status === 'ignore' ? 'status-ignore' : 'status-error'))">{{ log.status }}</span></td>
                                <td>{{ log.erp_object_id }}</td>
                                <td class="capitalize">Followup {{ log.direction === 'pull-from-followup' ? '&#8594;' : '&#8592;' }} {{ erpIntegrationName }}</td>
                                <td>{{ log.details }}</td>
                            </tr>
                            </tbody>
                        </table>
                        <div v-if="!loader && pagination.meta.total > pagination.meta.per_page" class="flex justify-content-center mt-5">
                            <b-pagination
                                @change="goToPage"
                                v-model="pagination.meta.current_page"
                                :total-rows="pagination.meta.total"
                                :per-page="pagination.meta.per_page"
                                first-text="First"
                                prev-text="Prev"
                                next-text="Next"
                                last-text="Last"
                            >
                            </b-pagination>
                        </div>
                    </div>
                    <div class="modal-footer bg-white  p-2 px-3">
                        <button type="button" class="btn btn-secondary font-medium" data-dismiss="modal">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { erpObjectOptions } from "../../../constants";
export default {
    name: 'erp-sync-setting-logs',
    props: ['erpIntegrationId', 'connectedErpIntegration', 'erpIntegrationName', 'vistaConnection'],
    data() {
        return {
            syncLogs: [],
            pagination: {
                meta: {},
                links: {},
            },
            loader: false,
            erpObjectOptions: erpObjectOptions,
            filterOptions: {
                status: [
                    { text: 'Success', value: 'success' },
                    { text: 'Error', value: 'error' }
                ],
                followupObject: [
                    { text: 'Project', value: 'project' },
                    { text: 'Company', value: 'company' },
                    { text: 'Contact', value: 'contact' },
                    { text: 'Residential Contact', value: 'residential-contact' },
                    { text: 'Change Order', value: 'change-order' }
                ],
                erpObject: []
            },
            filterSelected: {
                status: [],
                followupObject: [],
                erpObject: [],
                search: null,
                timeout: null
            },
            selectedLogIds: [],
            userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
    },
    mounted() {
        this.manageERPObjectFilterOptions();
    },
    watch: {
        'filterSelected.status': {
            handler: function(val, oldVal) {
                this.getERPSyncSettingLogs();
            }
        },
        'filterSelected.followupObject': {
            handler: function(val, oldVal) {
                this.getERPSyncSettingLogs();
            }
        },
        'filterSelected.erpObject': {
            handler: function(val, oldVal) {
                this.getERPSyncSettingLogs();
            }
        }
    },
    computed: {
        filterStatusLabel() {
            if(this.filterSelected.status.length > 0) {
                return 'Status (' + this.filterSelected.status.length + ')';
            }
            return 'Status';
        },
        filterFollowupObjectLabel() {
            if(this.filterSelected.followupObject.length > 0) {
                return 'Followup Object (' + this.filterSelected.followupObject.length + ')';
            }
            return 'Followup Object';
        },
        filterERPObjectLabel() {
            if(this.filterSelected.erpObject.length > 0) {
                return this.erpIntegrationName + ' Object (' + this.filterSelected.erpObject.length + ')';
            }
            return this.erpIntegrationName + ' Object';
        },
        ignoreButtonEnabled() {
            return this.isErrorLogIdsSelected;
        },
        retrySyncButtonEnabled() {
            return this.isErrorLogIdsSelected;
        },
        isErrorLogIdsSelected() {
            let self = this;
            const errorStatusIds = _.map(_.filter(self.syncLogs, { status: 'error' }), 'id');
            return _.every(self.selectedLogIds, id => _.includes(errorStatusIds, id));
        }
    },
    methods: {
        openSyncSettingLogsModal() {
            $('#erpSyncSettingLogsModal').modal('show');
            this.getERPSyncSettingLogs();
        },
        closeSyncSettingLogsModal() {
            $('#erpSyncSettingLogsModal').modal('hide');
        },
        goToPage(page) {
            this.getERPSyncSettingLogs(page);
        },
        getERPSyncSettingLogs(page = 1) {
            let self = this;
            self.loader = true;
            const payload = {
                page,
                filters: JSON.stringify(self.filterSelected),
                connectionId: self.vistaConnection ? self.vistaConnection.id : null
            };

            axios.get(route('api.erp.sync-setting-logs.index', payload)).then((response) => {
                self.loader = false;
                self.syncLogs = response.data.data;
                self.pagination.meta = response.data.meta;
                self.pagination.links = response.data.links;
            }).catch((error) => {
                self.loader = false;
                console.error("Error: " + error);
            });
        },
        formattedTime(syncTime) {
            return moment.utc(syncTime).local().format('D MMM YYYY h:mm A');
        },
        followupObjectUrl(log) {
            let url = '#';

            switch(log.followup_object) {
                case 'project':
                    url = route('project.edit', {project: log.followup_object_id}) + `?project=1`;
                    break;
                case 'company':
                    url = route('company.show', {company: log.followup_object_id});
                    break;
                case 'residential-contact':
                    url = route('contact.edit', {company: log.followup_residential_company_id, contact: log.followup_object_id});
                    break;
                case 'change-order':
                    url = route('project.edit', {project: log.followup_project_id}) + `?project=1`;
                    break;
            }

            return url;
        },
        clearFilters() {
            this.filterSelected.status = [];
            this.filterSelected.followupObject = [];
            this.filterSelected.erpObject = [];
            this.filterSelected.search = null;
            this.filterSelected.timeout = null;
            this.getERPSyncSettingLogs();
        },
        debounceFilter() {
            clearTimeout(this.filterSelected.timeout);
            this.filterSelected.timeout = setTimeout(() => {
                this.getERPSyncSettingLogs();
            }, 700, this);
        },
        getConnectedIntegration() {
            return (this.connectedErpIntegration === 'sage-100-contractor' ? 'sage100' : (this.connectedErpIntegration === 'sage-300-cre' ? 'sage300' : this.connectedErpIntegration))
        },
        manageERPObjectFilterOptions() {
            const connectedIntegration = this.getConnectedIntegration();
            const thirdPartyObjects = this.erpObjectOptions[connectedIntegration];
            this.filterOptions.erpObject = thirdPartyObjects.map(item => ({
                text: item.label,
                value: item.value
            }));
        },
        formatERPObjectLabel(value) {
            const connectedIntegration = this.getConnectedIntegration();
            const thirdPartyObjects = this.erpObjectOptions[connectedIntegration];
            const matchedObject = _.find(thirdPartyObjects, ['value', value]);
            return matchedObject?.label;
        },
        manageLogIdsSelection() {
            if(this.selectedLogIds.length === this.syncLogs.length) {
                this.selectedLogIds = [];
            }
            else {
                this.selectedLogIds = _.map(this.syncLogs, 'id');
            }
        },
        ignoreSelectedErrorLogs() {
            let self = this;

            axios.post(route('api.erp.sync-setting-logs.ignore-selected-error-logs'), {selectedLogIds: JSON.stringify(self.selectedLogIds)}).then((data) => {
                self.getToast().fire({
                    type: "success",
                    title: 'Selected error logs marked ignore successfully!'
                });
                self.clearSelectedLogIds();
                self.getERPSyncSettingLogs();
            }).catch((error) => {
                console.error(error);
            });
        },
        ignoreAllErrorLogs() {
            let self = this;

            Swal.fire({
                title: 'Are you sure you want to ignore all errors?',
                text: "You won't be able to revert this!",
                type: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#01B1AF',
                confirmButtonText: 'Yes, ignore all errors!'
            }).then((result) => {
                if(result.value) {
                    axios.post(route('api.erp.sync-setting-logs.ignore-all-error-logs'), {connectedErpIntegration: self.connectedErpIntegration}).then((data) => {
                        self.getToast().fire({
                            type: "success",
                            title: 'All error logs marked ignore successfully!'
                        });
                        self.getERPSyncSettingLogs();
                    }).catch((error) => {
                        console.log("error", error);
                    })
                }
            });
        },
        exportAllErrorLogs() {
            let self = this;
            const payload = {
                userTimeZone: self.userTimeZone,
                connectedErpIntegration: self.connectedErpIntegration,
                connectionId: self.vistaConnection? self.vistaConnection.id : null
            };

            axios.post(route('api.erp.sync-setting-logs.export-all-error-logs'), payload).then((data) => {
                this.getToast().fire({
                    type: 'success',
                    title: data.data
                });
            }).catch((error) => {
                console.error(error);
            });
        },
        retrySync() {
            let self = this;

            axios.post(route('api.erp.sync-setting-logs.retry-sync'), {selectedLogIds: JSON.stringify(self.selectedLogIds)}).then((data) => {
                self.getToast().fire({
                    type: "success",
                    title: 'Retry sync initiated successfully!'
                });
                self.clearSelectedLogIds();
                self.getERPSyncSettingLogs();
            }).catch((error) => {
                console.error(error);
            });
        },
        clearSelectedLogIds() {
            this.selectedLogIds = [];
            $("#selectAllLogsCheckbox").prop("checked", false);
        }
    },
}
</script>
<style scoped>
.status-badge {
    padding: 5px 10px;
    border-radius: 5px;
    font-weight: normal;
    font-size: 13px;
}
.status-success {
    background-color: #d4edda;
    color: #2d8042;
}
.status-error {
    background-color: #fe757552;
    color: #c33333;
}
.status-ignore {
    background-color: #6c757ddb;
    color: #fff;
}
#erpSyncLogsTable td, #erpSyncLogsTable th {
    border-left: none;
    border-right: none;
}
.loader-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.7);
    width: 100%;
    height: 100%;
}
#erpSyncLogsTable thead th {
    background-color: #e6ecf0 !important;
    font-size: 14px !important;
}
#erpSyncLogsTable tbody td {
    font-size: 14px !important;
}
.show-filter-selected {
    background: #c7eeee7d;
    padding: 8px 15px;
    border-radius: 3px;
}
.filter-popover-class {
    .popover-body {
        height: auto !important;
        padding: 0 !important;
    }
}
.hover\:theme-custom-background:hover {
    background-color: #01b1af2e;
}
.hover\:theme-custom-color:hover {
    color: #49545a;
}
.custom-checkbox {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    user-select: none;
    gap: 8px;
}
.custom-checkbox input[type="checkbox"] {
    display: none;
}
.custom-checkbox .checkmark {
    width: 20px;
    height: 20px;
    background-color: #fff;
    border: 2px solid #01B1AF;
    border-radius: 4px;
    display: inline-block;
    position: relative;
    transition: background-color 0.3s, border-color 0.3s;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark {
    background-color: #01B1AF;
    border-color: #01B1AF;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark::after {
    content: '';
    position: absolute;
    top: 45%;
    left: 50%;
    width: 7px;
    height: 13px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: translate(-50%, -50%) rotate(45deg);
}
.min-w-15 {
    min-width: 15%;
}
.search-container {
    position: relative;
}
.search-container i {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}
.modal-fullscreen-custom {
    width: 100vw;
    max-width: 100%;
    height: 100vh;
    margin: 0;
}

.modal-fullscreen-custom .modal-content {
    height: 100%;
    border-radius: 0;
}
</style>
